import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {NetworkContext} from '../../../lib/context';
import {
    ContentSwitcher,
    DataTable,
    DataTableSkeleton,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@carbon/react';
import CustomPagination from '../../../../../components/custom-pagination/CustomPagination';
import NoResultsFound from '../../../../../components/no-results-found';
import {observer} from 'mobx-react';
import {reaction} from 'mobx';
import {type ViewType} from '../useReportsPageController';
import {useSorting, SORT_ORDER} from '../../../lib/useSorting';
import {networkOptimization} from '../../../../../lib/api';
import {maHeaders} from './roster-tab-headers/maHeaders';
import {qhpHeaders} from './roster-tab-headers/qhpHeaders';
import {ParsedApiError} from '../../../../../lib/error-handler';
import handleError from '../../../../../lib/error';
import ErrorDisplay from '../../../components/error-display';

const PAGE_SIZE = 15;

interface IRosterTabContentProps {
    selectedViewType: ViewType;
    onSelectedViewTypeChange: (viewType: ViewType) => void;
    currentViewTypes: ViewType[];
    generateReportButton: React.ReactNode;
}

const RosterTabContent = observer((props: IRosterTabContentProps) => {
    const {selectedViewType, onSelectedViewTypeChange, currentViewTypes, generateReportButton} = props;

    const [providers, setProviders] = useState<any[]>([]);
    const noStore = useContext(NetworkContext);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<ParsedApiError | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [rowCount, setRowCount] = useState<number>(0);
    const {sortState, sortRows, resetSort} = useSorting({column_name: 'original_vs_added', direction: 'ASC'});
    const previousViewTypeIndex = useRef(selectedViewType.index);

    // filterKey is incremented by a MobX reaction whenever any filter value changes.
    // This guarantees the fetch effect fires even when the observer HOC misses a synchronous update.
    const [filterKey, setFilterKey] = useState(0);
    useEffect(() => {
        return reaction(
            () => [
                noStore.appliedGeographyFilter.state,
                noStore.appliedGeographyFilter.county,
                noStore.appliedGeographyFilter.standard_type,
                noStore.appliedGeographyFilter.standard_version,
                noStore.appliedGeographyFilter.patient_roster_name,
                noStore.specialtyFilterCode,
            ],
            () => setFilterKey(prev => prev + 1),
        );
    }, [noStore]);

    const headers = useMemo(() => {
        return noStore.appliedGeographyFilter.standard_type === 'MA' ? maHeaders : qhpHeaders;
    }, [noStore.appliedGeographyFilter.standard_type]);

    const revisionId = noStore.revisionId;
    const rosterNumber = noStore.runId!;

    // Reset page when filters change
    useEffect(() => {
        setPage(0);
    }, [
        rosterNumber,
        revisionId,
        selectedViewType,
        pageSize,
        noStore.appliedGeographyFilter.county,
        noStore.appliedGeographyFilter.state,
        noStore.appliedGeographyFilter.standard_type,
        noStore.appliedGeographyFilter.standard_version,
        noStore.appliedGeographyFilter.patient_roster_name,
        noStore.specialtyFilterCode,
        filterKey,
        sortState,
    ]);

    // Reset sort when view type changes (skip initial mount)
    useEffect(() => {
        if (previousViewTypeIndex.current !== selectedViewType.index) {
            resetSort();
            previousViewTypeIndex.current = selectedViewType.index;
        }
    }, [
        selectedViewType.index,
        resetSort,
    ]);

    useEffect(() => {
        let isActive = true;
        const controller = new AbortController();

        if (noStore.appliedGeographyFilter.standard_type !== '') {
            if (isActive) {
                setLoading(true);
                setError(null);
            }
            networkOptimization
                .fetchProviderRoster(
                    noStore.geographyFilters,
                    rosterNumber,
                    revisionId,
                    page + 1,
                    pageSize,
                    selectedViewType.name,
                    noStore.specialtyFilterCode,
                    controller.signal,
                    sortState
                        ? {
                            direction: sortState.direction,
                            column_name: sortState.column_name,
                        }
                        : undefined,
                )
                .then((result) => {
                    if (isActive) {
                        setProviders(
                            result.providerRosterData.map((p: any, i: number) => ({
                                ...p,
                                id: `${p.npi}${i}`,
                            })),
                        );
                        if (result.totalRows !== undefined) setRowCount(result.totalRows);
                    }
                })
                .catch((_err) => {
                    if (!controller.signal.aborted && isActive) {
                        const parsedError = handleError(_err as Error, null, 'Error fetching provider roster');
                        setError(parsedError);
                    }
                })
                .finally(() => {
                    if (isActive) setLoading(false);
                });
        }
        return () => {
            isActive = false;
            controller.abort();
        };
    }, [
        rosterNumber,
        page,
        pageSize,
        selectedViewType,
        noStore.appliedGeographyFilter.county,
        noStore.appliedGeographyFilter.state,
        noStore.appliedGeographyFilter.standard_type,
        noStore.appliedGeographyFilter.standard_version,
        noStore.appliedGeographyFilter.patient_roster_name,
        noStore.specialtyFilterCode,
        noStore.revisionId,
        filterKey,
        sortState,
    ]);

    if (loading)
        return (
            <DataTableSkeleton
                headers={headers}
                rowCount={PAGE_SIZE}
                columnCount={headers.length}
                compact
                showToolbar={false}
            />
        );

    if (error) {
        return <ErrorDisplay error={error} title='Unable to Load Roster Data' />;
    }

    return (
        <DataTable rows={providers} headers={headers} isSortable size='sm'>
            {({
                rows,
                headers,
                getHeaderProps,
                getRowProps,
                getTableProps,
                getTableContainerProps,
            }) => {
                return (
                    <TableContainer
                        data-testid='roster-table-container'
                        title={<div className='mt-[-1.25rem] mb-[-.75rem]'>Roster</div>}
                        description={
                            <>
                                <div className='mb-4'>
                                    Select providers/facilities to include in this roster
                                </div>
                                <div className='content-switcher-container'>
                                    <ContentSwitcher
                                        className='mb-4 !justify-normal'
                                        onChange={({index}) => {
                                            onSelectedViewTypeChange(currentViewTypes[index!]);
                                        }}
                                        selectedIndex={selectedViewType.index}
                                        size={'sm'}
                                    >
                                        {currentViewTypes.map((viewType) => {
                                            return (
                                                <Switch
                                                    key={viewType.name + viewType.index + viewType.text}
                                                    name={viewType.name}
                                                    text={viewType.text}
                                                    index={viewType.index}
                                                />
                                            );
                                        })}
                                    </ContentSwitcher>
                                    <div>
                                        {generateReportButton}
                                    </div>
                                </div>
                            </>
                        }
                        {...getTableContainerProps()}
                        className='overflow-none'
                    >
                        <Table {...getTableProps()} className='overflow-hidden'>
                            <TableHead>
                                <TableRow>
                                    {headers.map((header: any, i) => {
                                        let headerProps = getHeaderProps({header});
                                        const sortKey = header.sortKey || header.key;
                                        const isSortHeader = sortState?.column_name === sortKey;
                                        const currentSortDirection = isSortHeader && sortState
                                            ? sortState.direction
                                            : SORT_ORDER[0];

                                        return (
                                            <TableHeader
                                                {...headerProps}
                                                key={i}
                                                isSortHeader={isSortHeader}
                                                sortDirection={currentSortDirection}
                                                onClick={sortRows(sortKey)}
                                            >
                                                {header.header}
                                            </TableHeader>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => {
                                    const rowProps = getRowProps({row});
                                    return (
                                        <TableRow {...rowProps} key={row.id}>
                                            {row.cells.map((cell) => (
                                                <TableCell key={cell.id}>
                                                    {cell.value}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        {rows.length === 0 && (
                            <NoResultsFound />
                        )}
                        <CustomPagination
                            totalItems={rowCount}
                            pageSize={pageSize}
                            page={page}
                            pageSelectorType='Input'
                            onPageSizeChange={(newPageSize: number) => {
                                setPageSize(newPageSize);
                            }}
                            onPageChange={(newPage: number) => {
                                setPage(newPage);
                            }}
                        />
                    </TableContainer>
                );
            }}
        </DataTable>
    );
});

export default RosterTabContent;
